import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import useDictatedInput from '../../../chatView/hooks/useDictatedInput';
import { stopActiveDictation } from '../../../chatView/hooks/useSpeechRecognition';
import { trackMixpanelEvent } from '../../../utils/general';
import { AI_PROMPT_SUGGESTIONS_PREFIX } from '../../../chatView/aiPromptSuggestions';
import { MIXPANEL_PREFIX, SUGGESTIONS, TRANSLATION_PREFIX } from './consts';
import css from './style.css';

// Keep in sync with .panel width in style.css.
const PANEL_WIDTH = 580;
const PANEL_MARGIN = 16;
const PANEL_GAP = 8;

function AskSingularAICommandBar({ anchorRef, prompt, isStarting, onType, pickSuggestion, submit, close, translate }) {
    const inputRef = useRef(null);
    const panelRef = useRef(null);
    const followTranscriptRef = useRef(false);
    const [anchorPos, setAnchorPos] = useState(null);
    const {
        isListening,
        isSupported,
        toggleDictation,
        stopDictation,
        onManualChange,
        finishDictation,
        markVoiceUnused,
    } = useDictatedInput({
        value: prompt,
        onChange: onType,
        onTranscript: () => {
            followTranscriptRef.current = true;
        },
    });

    useLayoutEffect(() => {
        if (!followTranscriptRef.current) return;
        followTranscriptRef.current = false;
        const input = inputRef.current;
        if (!input) return;
        input.setSelectionRange(input.value.length, input.value.length);
        input.scrollLeft = input.scrollWidth;
    }, [prompt]);

    useLayoutEffect(() => {
        const el = anchorRef?.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const maxLeft = window.innerWidth - PANEL_MARGIN - PANEL_WIDTH;
        const left = Math.max(PANEL_MARGIN, Math.min(rect.left, maxLeft));
        setAnchorPos({ top: Math.round(rect.bottom + PANEL_GAP), left: Math.round(left) });
    }, [anchorRef]);

    useEffect(() => {
        stopActiveDictation();
        const previouslyFocused = document.activeElement;
        inputRef.current?.focus();
        return () => {
            if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
        };
    }, []);

    const onPanelKeyDown = e => {
        if (e.key !== 'Tab') return;
        const focusables = Array.from(panelRef.current?.querySelectorAll('button, input') || []).filter(
            el => !el.disabled
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    const hasText = prompt.trim().length > 0;
    const showMic = isSupported && (isListening || !hasText);
    const showSend = !isListening && hasText;

    const onPickSuggestion = text => {
        stopDictation();
        markVoiceUnused();
        pickSuggestion(text);
        inputRef.current?.focus();
    };

    const onInputChange = e => {
        followTranscriptRef.current = false;
        onManualChange(e.target.value);
    };

    const onMicClick = () => {
        trackMixpanelEvent(MIXPANEL_PREFIX, 'Voice Toggled', { willListen: !isListening });
        toggleDictation();
    };

    const doSubmit = () => {
        submit({ usedVoice: finishDictation() });
    };

    const onKeyDown = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            doSubmit();
        }
    };

    const onScrimClick = () => {
        stopDictation();
        close();
    };

    return createPortal(
        <div className={css.overlay} onKeyDown={onPanelKeyDown}>
            <div className={css.scrim} onClick={onScrimClick} data-testid="ask-ai-scrim" />
            <div
                ref={panelRef}
                className={css.panel}
                role="dialog"
                aria-modal="true"
                aria-label={translate(`${TRANSLATION_PREFIX}.INPUT_LABEL`)}
                style={anchorPos ? { top: anchorPos.top, left: anchorPos.left, right: 'auto' } : undefined}
            >
                <div className={css.capsule}>
                    <span className={css.capsuleIcon}>
                        <Icon
                            name="sparkles"
                            size={IconSizes.MEDIUM}
                            variant={IconVariants.WHITE}
                            interactive={false}
                        />
                    </span>
                    <input
                        ref={inputRef}
                        className={css.input}
                        value={prompt}
                        readOnly={isStarting}
                        aria-label={translate(`${TRANSLATION_PREFIX}.INPUT_LABEL`)}
                        placeholder={translate(
                            `${TRANSLATION_PREFIX}.${isListening ? 'PLACEHOLDER_LISTENING' : 'PLACEHOLDER'}`
                        )}
                        onChange={onInputChange}
                        onKeyDown={onKeyDown}
                    />
                    {showMic && !isStarting && (
                        <button
                            type="button"
                            className={classNames(css.micButton, { [css.micActive]: isListening })}
                            onClick={onMicClick}
                            aria-label={translate(`${TRANSLATION_PREFIX}.VOICE`)}
                        >
                            <Icon
                                name="microphone"
                                size={IconSizes.SMALL}
                                variant={isListening ? IconVariants.WHITE : IconVariants.DARK}
                                interactive={false}
                            />
                        </button>
                    )}
                    {showSend && !isStarting && (
                        <button
                            type="button"
                            className={css.sendButton}
                            onClick={doSubmit}
                            aria-label={translate(`${TRANSLATION_PREFIX}.SEND`)}
                        >
                            <Icon name="arrow-up" size={IconSizes.SMALL} interactive={false} />
                        </button>
                    )}
                </div>

                {isStarting ? (
                    <div className={css.card}>
                        <span className={css.spinner} aria-hidden="true" />
                        <div>
                            <div className={css.startingTitle}>
                                <Translate id={`${TRANSLATION_PREFIX}.STARTING_TITLE`} />
                            </div>
                            <div className={css.startingSubtitle}>
                                <Translate id={`${TRANSLATION_PREFIX}.STARTING_SUBTITLE`} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={css.suggestions}>
                        <div className={css.suggestionsHeading}>
                            <Translate id={`${TRANSLATION_PREFIX}.TRY_ASKING`} />
                        </div>
                        {SUGGESTIONS.map(({ id, icon }) => {
                            const text = translate(`${AI_PROMPT_SUGGESTIONS_PREFIX}.${id}`);
                            return (
                                <button
                                    type="button"
                                    key={id}
                                    className={css.suggestion}
                                    onClick={() => onPickSuggestion(text)}
                                >
                                    <Icon
                                        name={icon}
                                        size={IconSizes.SMALL}
                                        variant={IconVariants.ACTIVE}
                                        interactive={false}
                                    />
                                    <span>{text}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

AskSingularAICommandBar.propTypes = {
    anchorRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
    prompt: PropTypes.string.isRequired,
    isStarting: PropTypes.bool.isRequired,
    onType: PropTypes.func.isRequired,
    pickSuggestion: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(AskSingularAICommandBar);
